import React, { useEffect, useRef, useState } from 'react';
import { SearchPill, Help, Btn } from './host/kit';

/**
 * VenuePicker — optional place search for a signal, biased to the chosen city.
 * Regular (brutalist) restyle of the Portal venue picker. Uses the Mapbox Search
 * Box /forward endpoint (strong POI coverage) with a proximity bias to the city
 * and a distance filter so results stay in that city.
 *
 * Value: { name, address, lat, lng } | null. Venue is optional — a signal still
 * publishes at the city centroid when none is picked.
 *
 * `variant`: 'default' (the brutalist box, used by the signal / Portal pages) or 'pill'
 * (the VENUE row of the host event edit handoff, inside the wizard's `.hev` scope):
 * a grey search pill with a grey results panel, or once picked a black pill with the
 * name, address and a white CHANGE button.
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAX_KM_FROM_CITY = 80;

function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function prettyCategory(cat) {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const ELLIPSIS = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

export default function VenuePicker({ value, onChange, cityCoords, cityLabel, variant = 'default' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || !MAPBOX_TOKEN) { setResults([]); return undefined; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q, limit: '10', language: 'en', types: 'poi,address,place', access_token: MAPBOX_TOKEN,
        });
        if (cityCoords?.lng != null && cityCoords?.lat != null) {
          params.set('proximity', `${cityCoords.lng},${cityCoords.lat}`);
        }
        const data = await (await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${params.toString()}`)).json();
        let items = (data.features || []).map((f) => {
          const [lng, lat] = f.geometry?.coordinates || [];
          const p = f.properties || {};
          const name = p.name || p.name_preferred || '';
          const address = p.full_address || p.address || p.place_formatted || '';
          const category = Array.isArray(p.poi_category) ? p.poi_category[0] : null;
          return { name, address, lat, lng, category };
        }).filter((it) => it.name && typeof it.lat === 'number');
        if (cityCoords?.lat != null && cityCoords?.lng != null) {
          items = items.filter((it) => distanceKm(cityCoords.lat, cityCoords.lng, it.lat, it.lng) <= MAX_KM_FROM_CITY);
        }
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, cityCoords?.lat, cityCoords?.lng]);

  const pick = (v) => {
    onChange?.({ name: v.name, address: v.address, lat: v.lat, lng: v.lng });
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const town = (cityLabel || '').split(',')[0].trim();
  const placeholder = town ? `Search a place in ${town}…` : 'Search a place…';

  if (variant === 'pill') {
    if (value) {
      return (
        <div className="hev-row" data-round="" style={{ background: '#000', color: '#fff', padding: '10px 10px 10px 18px', gap: 12 }}>
          <span className="hev-row-text">
            <span style={{ fontSize: 13, fontWeight: 600, ...ELLIPSIS }}>{value.name}</span>
            {value.address && <span style={{ fontSize: 10, letterSpacing: '.1em', color: '#9CA3AF', ...ELLIPSIS }}>{value.address}</span>}
          </span>
          <Btn variant="white" size="xs" style={{ height: 32, padding: '0 14px', fontSize: 10 }} onClick={() => onChange?.(null)}>CHANGE</Btn>
        </div>
      );
    }

    const showList = !!cityCoords && !!query.trim();
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <SearchPill
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          disabled={!cityCoords}
          placeholder={placeholder}
          aria-label="Search a place"
        />
        {!cityCoords && <Help>Pick a city first to search venues.</Help>}
        {showList && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 6, borderRadius: 20, background: '#F5F5F5', maxHeight: 288, overflowY: 'auto' }}>
            {searching && results.length === 0 ? (
              <span style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#9CA3AF' }}>SEARCHING…</span>
            ) : results.length === 0 ? (
              <span style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#9CA3AF' }}>NO RESULTS</span>
            ) : (
              results.map((r, i) => (
                <button
                  key={`${r.name}-${i}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(-1)}
                  onFocus={() => setHoverIdx(i)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%', minWidth: 0,
                    padding: '10px 14px', border: 'none', borderRadius: 14, background: hoverIdx === i ? '#fff' : 'transparent',
                    color: '#000', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, maxWidth: '100%', ...ELLIPSIS }}>{r.name}</span>
                  {r.address && <span style={{ fontSize: 10, letterSpacing: '.1em', color: '#6B7280', maxWidth: '100%', ...ELLIPSIS }}>{r.address}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-start justify-between gap-3 border-2 border-black bg-white p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-black">@ {value.name}</p>
          {value.address && <p className="mt-0.5 truncate font-mono text-[11px] text-gray-500">{value.address}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange?.(null)}
          className="flex-shrink-0 font-mono text-[10px] font-bold uppercase tracking-wider underline hover:no-underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={!cityCoords}
        placeholder={cityLabel ? `Search a place in ${(cityLabel || '').split(',')[0]}…` : 'Search a place…'}
        className="w-full border-2 border-black bg-white px-4 py-3 font-mono text-sm text-black placeholder-gray-400 outline-none focus:ring-0 disabled:bg-gray-100 disabled:text-gray-400"
      />
      {!cityCoords && <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-500">Pick a city first to search venues.</p>}
      {open && cityCoords && (results.length > 0 || searching || query.trim()) && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto border-2 border-black bg-white">
          {searching && results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">No results</p>
          ) : (
            results.map((r, i) => (
              <button
                key={`${r.name}-${i}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                className="block w-full border-b border-gray-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-gray-50"
              >
                <span className="flex items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-black">{r.name}</span>
                  {r.category && <span className="flex-shrink-0 font-mono text-[9px] uppercase tracking-widest text-gray-500">{prettyCategory(r.category)}</span>}
                </span>
                {r.address && <span className="mt-0.5 block truncate font-mono text-[11px] text-gray-500">{r.address}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
